import React from 'react';
import { SentryErrorBoundary } from '../../../../services/SentryService';
import { Box, IconButton } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import SlideSidePane from '../../../../Views/Shared/SlideSidePane';
import { Content } from './Content';
import { Loading } from './Loading';
import { Error } from './Error';
import { useGetAppointmentQuery, type AppointmentUpdate } from '@features/appointments';
import { useAppointmentTypes } from '@features/appointment-types';
import { useNavigateToEncounter } from '../../hooks/useNavigateToEncounter';
import { useEventTracking } from '@services/statsig';

interface EditAppointmentProps {
  open: boolean;
  onClose: () => void;
  appointmentId?: string;
  onAppointmentUpdate?: (
    appointmentId: string,
    updates: AppointmentUpdate,
    seriesId?: string,
  ) => Promise<void>;
  onAppointmentDelete?: (appointmentId: string, seriesId?: string) => Promise<void>;
}

export const EditAppointment: React.FC<EditAppointmentProps> = ({
  open,
  onClose,
  appointmentId,
  onAppointmentUpdate,
  onAppointmentDelete,
}) => {
  const { navigateToEncounter, isLoading: isCreatingEncounter } = useNavigateToEncounter();
  const { appointment: appointmentTracking } = useEventTracking();

  // Only fetch when we have an appointmentId and the modal is open
  const {
    data: appointment,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetAppointmentQuery(
    { appointmentId: appointmentId! },
    {
      skip: !appointmentId || !open,
      refetchOnMountOrArgChange: true,
    },
  );

  const { isLoading: isAppointmentTypesLoading } = useAppointmentTypes();

  // Check if we're loading a new appointment (different from the current one)
  const isLoadingNewAppointment =
    isLoading ||
    (isFetching && appointment?.appointment_id !== appointmentId) ||
    isAppointmentTypesLoading;

  const handleRecordClick = async () => {
    if (!appointment) return;

    // Track the action before navigating
    appointmentTracking.actionClicked({
      appointment_id: appointment.appointment_id,
      action: 'view_session',
      source: 'appointment_edit',
    });

    try {
      await navigateToEncounter(appointment);
      // The navigation and cache invalidation is handled by the hook
    } catch (error) {
      console.error('Error navigating to encounter:', error);
      // Could show a toast notification here if we have a notification system
    }
  };

  // Wrap everything in the SlideSidePane
  return (
    <SlideSidePane
      open={open}
      onClose={onClose}
      title="Modify Appointment"
      action={
        !isLoadingNewAppointment &&
        appointment && (
          <IconButton
            onClick={handleRecordClick}
            aria-label="Go to appointment"
            disabled={isCreatingEncounter}
          >
            <LaunchIcon />
          </IconButton>
        )
      }
    >
      <SentryErrorBoundary
        fallback={<Error error={{ message: 'An unexpected error occurred' }} />}
        onError={(error, errorInfo) => {
          console.error('EditAppointment Error Boundary:', error, errorInfo);
        }}
      >
        {isLoadingNewAppointment || isFetching ? (
          <Loading />
        ) : error || !appointment ? (
          <Error error={error as Error | undefined} onRetry={() => refetch()} />
        ) : (
          <Content
            appointment={appointment}
            onAppointmentUpdate={onAppointmentUpdate!}
            onAppointmentDelete={onAppointmentDelete!}
            handleRecordClick={handleRecordClick}
          />
        )}
      </SentryErrorBoundary>
    </SlideSidePane>
  );
};
